import { InnerBlocks, useBlockProps } from '@wordpress/block-editor';

export default function Save({ attributes }) {
  const blockProps = useBlockProps.save({
    className: 'what-to-expect'
  });

  const { mainTitle, introHeading, stepsHeading, stepsIntro, steps } = attributes;

  return (
    <div {...blockProps}>
      {/* Main Title Section */}
      <div className="bg-primary text-white pt-16 pb-4 px-0 lg:px-6">
        <div className="max-w-content">
          <div className="gap-y-6">
            {mainTitle && (
              <h2 
                className="~text-2xl/4xl font-bold text-left text-white mb-8"
                dangerouslySetInnerHTML={{ __html: mainTitle }}
              />
            )}
            
            {introHeading && (
              <div className="text-lg leading-relaxed text-left">
                <p className="mb-4" dangerouslySetInnerHTML={{ __html: introHeading }} />
              </div>
            )}

            {stepsHeading && (
              <h3 
                className="~text-xl/3xl font-bold text-left my-6 text-white"
                dangerouslySetInnerHTML={{ __html: stepsHeading }}
              />
            )}
        
            {stepsIntro && (
              <div className="text-lg leading-relaxed text-left mb-6">
                <p className="mb-4" dangerouslySetInnerHTML={{ __html: stepsIntro }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Steps Section */}
      <div className="bg-primary text-white pt-0 pb-8">
        <div className="max-w-content mx-auto px-2 lg:px-4">
          {/* Interactive Steps */}
          {steps && steps.length > 0 && (
            <div className="">
              {/* Mobile Layout - Tabs at top of cards */}
              <div className="block lg:hidden">
                {/* Tab Navigation */}
                <div className="flex w-full overflow-x-auto scrollbar-hide gap-1 px-2" id="mobile-tabs">
                  {steps.map((step, index) => (
                    <button 
                      key={index}
                      type="button"
                      className={`step-tab-mobile -mb-1 flex-1 px-4 py-2 text-lg font-semibold transition-all duration-300 rounded-t-lg whitespace-nowrap bg-secondary-900 text-white border-transparent ${index === 0 ? 'active' : ''}`}
                      data-step-index={index}
                      data-step-number={index + 1}
                    >
                      <span className="step-label">{index === 0 ? `Step ${index + 1}` : (index + 1)}</span>
                    </button>
                  ))}
                </div>

                {/* Card Container for Step Content */}
                <div className="bg-white shadow-lg min-h-[240px] w-full">
                  {/* All step contents rendered for SEO */}
                  {steps.map((step, index) => (
                    <div key={index} className={`step-content-mobile p-8 ${index !== 0 ? 'hidden' : ''}`} data-step-index={index}>
                      <h3 className="~text-xl/3xl text-secondary-900 font-bold mb-6">
                        {step.subtitle || 'Step Title'}
                      </h3>
                      
                      {/* Content - Legacy text or Inner Blocks */}
                      <div className="text-lg leading-7 text-gray-700 mb-8">
                        {step.text ? (
                          // Legacy format: display text content
                          <div dangerouslySetInnerHTML={{ __html: step.text.replace(/\n/g, '<br>') }} />
                        ) : step.innerContent && step.innerContent.length > 0 ? (
                          // New format: render the step's inner blocks
                          step.innerContent.map((block, blockIndex) => (
                            <div key={blockIndex} dangerouslySetInnerHTML={{ 
                              __html: wp.blocks.getBlockContent(block) 
                            }} />
                          ))
                        ) : (
                          // Fallback: only for first step with no content
                          index === 0 && <InnerBlocks.Content />
                        )}
                      </div>
                      
                      {step.ctaText && step.ctaUrl && (
                        <span dangerouslySetInnerHTML={{ 
                          __html: `[arrow_link url="${step.ctaUrl}" text="${step.ctaText}"]` 
                        }} />
                      )}
                    </div>
                  ))}
                  
                  {/* Hidden SEO content - all steps visible to crawlers */}
                  <div className="sr-only" aria-hidden="true">
                    {steps.map((step, index) => (
                      <div key={index}>
                        <h3>{step.stepName || `Step ${index + 1}`}: {step.subtitle || ''}</h3>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Desktop Layout - Folder Bookmark Style Tabs */}
              <div className="max-w-content hidden lg:block">
                {/* Tab Navigation - Folder Bookmark Style */}
                <div className="flex justify-start gap-2 px-4 mb-0" id="desktop-tabs">
                  {steps.map((step, index) => (
                    <button 
                      key={index}
                      type="button"
                      className={`step-tab-desktop px-6 py-4 text-xl font-semibold transition-all duration-300 rounded-t-xl bg-secondary-900 text-white border-transparent hover:bg-secondary-800 ${index === 0 ? 'active' : ''}`}
                      data-step-index={index}
                    >
                      Step {index + 1}
                    </button>
                  ))}
                </div>

                {/* Card Container for Step Content */}
                <div className="bg-white shadow-lg w-full mx-0 px-8 py-8 rounded-tr-xl rounded-b-xl transition-all duration-300">
                  {/* All step contents rendered for SEO */}
                  {steps.map((step, index) => (
                    <div key={index} className={`step-content-desktop ${index !== 0 ? 'hidden' : ''}`} data-step-index={index}>
                      <h3 className="~text-2xl/4xl text-secondary-900 font-bold mb-6">
                        Step {index + 1}: {step.subtitle || 'Step Title'}
                      </h3>
                      
                      {step.subheading && (
                        <h4 className="text-xl font-semibold text-primary mb-4">
                          {step.subheading}
                        </h4>
                      )}
                      
                      {/* Content - Legacy text or Inner Blocks */}
                      <div className="text-lg leading-7 text-gray-700 mb-8 max-w-4xl">
                        {step.text ? (
                          // Legacy format: display text content
                          <div dangerouslySetInnerHTML={{ __html: step.text.replace(/\n/g, '<br>') }} />
                        ) : step.innerContent && step.innerContent.length > 0 ? (
                          // New format: render the step's inner blocks
                          step.innerContent.map((block, blockIndex) => (
                            <div key={blockIndex} dangerouslySetInnerHTML={{ 
                              __html: wp.blocks.getBlockContent(block) 
                            }} />
                          ))
                        ) : (
                          // Fallback: only for first step with no content
                          index === 0 && <InnerBlocks.Content />
                        )}
                      </div>
                      
                      {step.ctaText && step.ctaUrl && (
                        <span dangerouslySetInnerHTML={{ 
                          __html: `[arrow_link url="${step.ctaUrl}" text="${step.ctaText}"]` 
                        }} />
                      )}
                    </div>
                  ))}
                  
                  {/* Hidden SEO content - all steps visible to crawlers */}
                  <div className="sr-only" aria-hidden="true">
                    {steps.map((step, index) => (
                      <div key={index}>
                        <h3>{step.stepName || `Step ${index + 1}`}: {step.subtitle || ''}</h3>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
